import type { ReactNode } from "react";
import { motion, useReducedMotion } from "framer-motion";
import { useTranslation } from "react-i18next";
import { Divider } from "@/shared/ui/core";
import { cn } from "@/shared/lib/cn";
import { FooterBottomBar } from "./FooterBottomBar";
import { FooterBrandColumn } from "./FooterBrandColumn";
import { FooterColumn } from "./FooterColumn";
import {
  DEFAULT_CONTACT_CHANNELS,
  DEFAULT_SOCIALS,
  FooterContactColumn,
  type FooterSocial,
} from "./FooterContactColumn";
import type { FooterLinkItem } from "./FooterLink";

const SHOP_LINKS: readonly FooterLinkItem[] = [
  { label: "Catálogo", to: "/shop" },
  { label: "Paquetes", to: "/shop?categoria=paquetes" },
  { label: "Novedades", to: "/shop?orden=newest" },
  { label: "Mi carrito", to: "/carrito" },
];

const ACCOUNT_LINKS: readonly FooterLinkItem[] = [
  { label: "Iniciar sesión", to: "/login" },
  { label: "Crear cuenta", to: "/register" },
  { label: "Recuperar contraseña", to: "/forgot-password" },
  { label: "Centro de soporte", to: "/soporte" },
];

const LEGAL_LINKS: readonly FooterLinkItem[] = [
  { label: "Aviso de privacidad", to: "/legal#privacidad" },
  { label: "Términos y condiciones", to: "/legal#terminos" },
  { label: "Política de envío", to: "/legal#envios" },
  { label: "Devoluciones", to: "/legal#devoluciones" },
];

const MISSION =
  "Plataforma de bienestar con un modelo de venta directa sostenible. Productos premium, formación continua y la comunidad para hacer crecer tu negocio digital.";

const DISCLAIMER =
  "Suplementos alimenticios. Este producto no es un medicamento. Su consumo es responsabilidad de quien lo recomienda y de quien lo usa. Los testimonios son personales y no representan resultados garantizados.";

export interface SiteFooterProps {
  /**
   * El año del copyright. Por defecto, el de hoy — pero se puede fijar
   * desde una prueba para que no cambie el 1 de enero.
   */
  year?: number;
  shopLinks?: readonly FooterLinkItem[];
  accountLinks?: readonly FooterLinkItem[];
  legalLinks?: readonly FooterLinkItem[];
  contactChannels?: readonly FooterLinkItem[];
  socials?: readonly FooterSocial[];
  className?: string;
}

/**
 * Cada bloque del pie entra con el revelado de la referencia (Aceternity
 * "footer-section", 27-ago): desenfocado y un pelo arriba → nítido y en
 * su sitio, una sola vez, al entrar en pantalla. Con `prefers-reduced-
 * motion`, ningún movimiento: el contenido se pinta y ya.
 */
function AnimatedContainer({
  className,
  delay = 0.1,
  children,
}: {
  className?: string;
  delay?: number;
  children: ReactNode;
}) {
  const shouldReduceMotion = useReducedMotion();

  if (shouldReduceMotion) return <div className={className}>{children}</div>;

  return (
    <motion.div
      /*
       * Desde ABAJO y con más recorrido que la demo (24px, no 8): el
       * pie entra cuando el scroll LLEGA a él, y un desplazamiento de
       * 8px se perdía — parecía que no había animación (Anahí, 27-ago).
       * `amount: 0.2` dispara cuando un quinto del bloque ya se ve, no
       * con el primer píxel.
       */
      initial={{ filter: "blur(6px)", translateY: 24, opacity: 0 }}
      whileInView={{ filter: "blur(0px)", translateY: 0, opacity: 1 }}
      viewport={{ once: true, amount: 0.2 }}
      transition={{ delay, duration: 0.8, ease: [0.25, 0.1, 0.25, 1] }}
      className={className}
    >
      {children}
    </motion.div>
  );
}

/**
 * El pie del sitio — rediseño del 27-ago sobre la referencia
 * "footer-section" de Aceternity, con la ropa de It's The Best: la caja
 * centrada con las esquinas superiores redondeadas, el RESPLANDOR radial
 * violeta que nace del borde superior, y las columnas entrando en
 * cascada con el revelado de arriba. El contenido es el de siempre:
 * marca (UNA vez — el imagotipo ya trae el nombre), misión, CTA, los
 * tres bloques de enlaces, la línea legal y el descargo.
 *
 * ── Por qué trae su propio contenido ─────────────────────────────────
 * Es el pie del SITIO, el mismo en la tienda y en el panel. Si cada
 * pantalla tuviera que pasarle sus enlaces, dos pantallas acabarían con
 * pies distintos sin que nada avisara. Todo es sobrescribible por props
 * para el caso raro; el caso normal es no pasar nada.
 */
export function SiteFooter({
  year = new Date().getFullYear(),
  shopLinks = SHOP_LINKS,
  accountLinks = ACCOUNT_LINKS,
  legalLinks = LEGAL_LINKS,
  contactChannels = DEFAULT_CONTACT_CHANNELS,
  socials = DEFAULT_SOCIALS,
  className,
}: SiteFooterProps) {
  const { t } = useTranslation();

  return (
    /*
     * La SUPERFICIE cubre todo el ancho (Anahí, 27-ago: la caja centrada
     * dejaba dos hombros de fondo a los lados); el CONTENIDO sigue
     * acotado a `max-w-6xl` adentro. El resplandor radial y la chispa
     * del borde superior se quedan — centrados sobre la página entera.
     */
    <footer
      className={cn(
        "relative w-full overflow-hidden border-t border-ds-border bg-ds-cream-2/70",
        "bg-[radial-gradient(35%_128px_at_50%_0%,rgba(92,79,214,0.10),transparent)]",
        className,
      )}
    >
      {/* La chispa: una línea de luz centrada sobre el borde. */}
      <div
        aria-hidden="true"
        className="absolute left-1/2 top-0 h-px w-1/3 -translate-x-1/2 -translate-y-1/2 rounded-full bg-ds-violet-pop/40 blur"
      />

      <div className="relative mx-auto w-full max-w-6xl px-6 py-12 sm:px-8 lg:py-16">
        <div className="grid grid-cols-1 gap-10 sm:grid-cols-2 lg:grid-cols-5">
          <AnimatedContainer className="lg:col-span-2">
            <FooterBrandColumn
              mission={MISSION}
              ctaLabel="Únete al programa"
              ctaTo="/register"
            />
          </AnimatedContainer>
          <AnimatedContainer delay={0.2}>
            <FooterColumn title="Tienda" links={shopLinks} />
          </AnimatedContainer>
          <AnimatedContainer delay={0.3}>
            <FooterColumn title="Cuenta y soporte" links={accountLinks} />
          </AnimatedContainer>
          <AnimatedContainer delay={0.4}>
            <FooterContactColumn title="Contacto" channels={contactChannels} socials={socials} />
          </AnimatedContainer>
        </div>

        <Divider className="my-10" />

        <AnimatedContainer delay={0.2}>
          <FooterBottomBar year={year} legalLinks={legalLinks} />

          <p className="mt-8 text-[11px] leading-relaxed text-ds-ink-3">
            {t(DISCLAIMER)}
          </p>
        </AnimatedContainer>
      </div>
    </footer>
  );
}
